import { Controller } from "@hotwired/stimulus"

// Handles drag-and-drop reordering of active tasks.
// Attached to the #tasks container, each .task-item becomes draggable.
export default class extends Controller {
  static values = {
    url: String // PATCH endpoint for persisting order
  }

  connect() {
    this.draggedEl = null
    this.placeholder = null
    this.bindItems()

    // Re-bind after Turbo Stream updates
    this.observer = new MutationObserver(() => this.bindItems())
    this.observer.observe(this.element, { childList: true })
  }

  disconnect() {
    this.observer?.disconnect()
  }

  bindItems() {
    this.element.querySelectorAll(".task-item:not([data-completed='true'])").forEach(item => {
      if (item.dataset.sortableBound) return
      item.dataset.sortableBound = "true"
      item.setAttribute("draggable", "true")

      item.addEventListener("dragstart", (e) => this.onDragStart(e, item))
      item.addEventListener("dragend", (e) => this.onDragEnd(e, item))
      item.addEventListener("dragover", (e) => this.onDragOver(e, item))
      item.addEventListener("dragenter", (e) => e.preventDefault())
      item.addEventListener("drop", (e) => this.onDrop(e, item))

      // Touch support
      item.addEventListener("touchstart", (e) => this.onTouchStart(e, item), { passive: false })
      item.addEventListener("touchmove", (e) => this.onTouchMove(e, item), { passive: false })
      item.addEventListener("touchend", (e) => this.onTouchEnd(e, item))
    })
  }

  // ── Drag Events ──

  onDragStart(e, item) {
    this.draggedEl = item
    item.classList.add("dragging")
    e.dataTransfer.effectAllowed = "move"
    e.dataTransfer.setData("text/plain", item.dataset.taskId)
  }

  onDragEnd(_e, item) {
    item.classList.remove("dragging")
    this.removePlaceholder()
    this.draggedEl = null
  }

  onDragOver(e, item) {
    e.preventDefault()
    e.dataTransfer.dropEffect = "move"

    if (!this.draggedEl || item === this.draggedEl) return

    const rect = item.getBoundingClientRect()
    const midY = rect.top + rect.height / 2

    if (e.clientY < midY) {
      item.parentNode.insertBefore(this.draggedEl, item)
    } else {
      item.parentNode.insertBefore(this.draggedEl, item.nextSibling)
    }
  }

  onDrop(e, _item) {
    e.preventDefault()
    this.persistOrder()
  }

  // ── Touch Events (mobile) ──

  onTouchStart(e, item) {
    // Only start drag on long press (300ms hold)
    this.touchTimer = setTimeout(() => {
      this.draggedEl = item
      item.classList.add("dragging")
      this.touchActive = true

      // Haptic feedback if available
      if (navigator.vibrate) navigator.vibrate(30)
    }, 300)

    this.touchStartY = e.touches[0].clientY
    this.touchMoved = false
  }

  onTouchMove(e, _item) {
    // Cancel long-press if finger moves before drag starts
    if (!this.touchActive) {
      if (Math.abs(e.touches[0].clientY - this.touchStartY) > 10) {
        clearTimeout(this.touchTimer)
      }
      return
    }

    e.preventDefault()
    const touch = e.touches[0]
    this.touchMoved = true

    // Find element under finger
    // Temporarily hide dragged element to get element underneath
    this.draggedEl.style.pointerEvents = "none"
    const target = document.elementFromPoint(touch.clientX, touch.clientY)
    this.draggedEl.style.pointerEvents = ""

    const taskItem = target?.closest(".task-item")
    if (taskItem && taskItem !== this.draggedEl && taskItem.dataset.completed !== "true") {
      const rect = taskItem.getBoundingClientRect()
      const midY = rect.top + rect.height / 2

      if (touch.clientY < midY) {
        taskItem.parentNode.insertBefore(this.draggedEl, taskItem)
      } else {
        taskItem.parentNode.insertBefore(this.draggedEl, taskItem.nextSibling)
      }
    }
  }

  onTouchEnd(_e, _item) {
    clearTimeout(this.touchTimer)

    if (this.touchActive && this.draggedEl) {
      this.draggedEl.classList.remove("dragging")
      this.persistOrder()
    }

    this.touchActive = false
    this.draggedEl = null
  }

  // ── Helpers ──

  removePlaceholder() {
    this.placeholder?.remove()
    this.placeholder = null
  }

  persistOrder() {
    const taskIds = Array.from(
      this.element.querySelectorAll(".task-item:not([data-completed='true'])")
    ).map(el => parseInt(el.dataset.taskId))

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    fetch(this.urlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
        "Accept": "application/json"
      },
      body: JSON.stringify({ task_ids: taskIds })
    }).catch(err => {
      console.error("Failed to save task order:", err)
    })
  }
}
